import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gameTableService } from "../api/services/gameTables/gameTableService";
import {
  CreateGameTableRequest,
  UpdateGameTableRequest,
  GetGameTablesParams,
} from "../api/services/gameTables/type";

export const gameTableKeys = {
  all: ["gameTables"] as const,
  lists: () => [...gameTableKeys.all, "list"] as const,
  list: (params: GetGameTablesParams) =>
    [...gameTableKeys.lists(), params] as const,
  details: () => [...gameTableKeys.all, "detail"] as const,
  detail: (id: string) => [...gameTableKeys.details(), id] as const,
};

export const useGameTables = (params: GetGameTablesParams = {}) =>
  useQuery({
    queryKey: gameTableKeys.list(params),
    queryFn: () => gameTableService.getGameTables(params),
  });

export const useGameTable = (id: string) => {
  return useQuery({
    queryKey: gameTableKeys.detail(id),
    queryFn: () => gameTableService.getGameTable(id),
    enabled: !!id,
  });
};

export const useCreateGameTable = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateGameTableRequest) =>
      gameTableService.createGameTable(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameTableKeys.lists() }),
  });
};

export const useUpdateGameTable = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGameTableRequest }) =>
      gameTableService.updateGameTable(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameTableKeys.all }),
  });
};

export const useDeleteGameTable = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gameTableService.deleteGameTable(id),
    onSuccess: (_, deletedId) => {
      qc.removeQueries({ queryKey: gameTableKeys.detail(deletedId) });
      qc.invalidateQueries({ queryKey: gameTableKeys.lists() });
    },
  });
};
